// /home/app/apps/frontend/src/index.tsx
import '@theme-css/index.css'; // all design CSS from packages/theme
import React, { PropsWithChildren } from 'react';
import { createRoot } from 'react-dom/client';
import { initTheme } from '@theme';

// React Query
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// App
import App from './app/App';

// Providers
import TelegramProvider from './app/providers/TelegramProvider';
import SessionProvider from './app/providers/SessionProvider';
import ProductsProvider from './app/providers/ProductsProvider';
import ToastProvider from './app/providers/ToastProvider';

// Initialize theme variables and mode (light/dark) once
initTheme();

// Singleton QueryClient (stable across HMR)
declare global {
  interface Window { __queryClient?: QueryClient }
}
const queryClient =
  window.__queryClient ??
  (window.__queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  }));

function AppProviders({ children }: PropsWithChildren<{}>) {
  return (
    <TelegramProvider>
      <QueryClientProvider client={queryClient}>
        <SessionProvider>
          <ProductsProvider>
            <ToastProvider>{children}</ToastProvider>
          </ProductsProvider>
        </SessionProvider>
      </QueryClientProvider>
    </TelegramProvider>
  );
}

// Support either #app or #root in index.html
const container = document.getElementById('app') || document.getElementById('root');
if (!container) throw new Error('Root element #app or #root not found');

createRoot(container).render(
  <React.StrictMode>
    <AppProviders>
      <App />
    </AppProviders>
  </React.StrictMode>
);
